<template>
  <div class="eraby">
    <div
      class="eraby__wrapper"
      :class="[
        {
          'eraby--eraby__wrapper': disabled
        }
      ]"
    >
      <div
        v-if="labelText"
        class="eraby__label"
        :class="[
          {
            'eraby__label--error': error,
            'eraby__label--disabled': disabled,
            'eraby__label--active': isLabelActive
          }
        ]"
      >
        <span v-if="hint"
              class="eraby__hint"
              :title="hint">
          <vsx-icon color="#1A1A1A"
                    icon-name="InfoCircle"
                    :size="16"
                    type="linear" />
        </span>
        {{ labelText }}
        <span v-if="required"
              class="eraby__required">*</span>
      </div>
      <vue-multiselect
        v-model="selectedItems"
        :class="[`multiselect--${error && 'error'}`, `multiselect--${disabled && 'disabled'}`]"
        :disabled="disabled"
        :limit="limit"
        :loading="loading"
        :label="label"
        :multiple="multiple"
        :options="options"
        :placeholder="placeholder"
        :close-on-select="!multiple"
        :searchable="searchable"
        v-bind="$attrs"
        ref="multiselect"
        tag-placeholder="Add this as new tag"
        :taggable="taggable"
        :track-by="trackBy"
        @close="handleLabelDeactivate"
        @open="handleLabelActive"
        @search-change="handleAsyncSearch"
        @tag="addTag"
      >
        <template #tag="{ option, remove }">
          <div class="eraby__selected">
            {{ option[label] }}
            <button type="button"
                    @click="remove(option)">
              <vsx-icon color="#808080"
                        icon-name="CloseCircle"
                        :size="16"
                        type="linear" />
            </button>
          </div>
        </template>
        <template #singleLabel="props">
          <slot v-if="$slots.singleLabel"
                name="singleLabel"
                :single-label="props" />
        </template>
        <template #selection="props">
          <slot v-if="$slots.selection"
                name="selection"
                :selection="props" />
        </template>
        <template #option="props">
          <slot v-if="$slots.option"
                name="option"
                :option="props" />
          <div
            v-else
            style="width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
          >
            <template v-if="props.option.isTag">
              <slot v-if="$slots.tag"
                    name="tag"
                    :tag="props.option.label" />
              <template v-else>
                {{ props.option.label }}
              </template>
            </template>
            <template v-else>
              {{ props.option[label] }}
            </template>
          </div>
        </template>
        <template #caret="{ toggle }">
          <button
            class="multiselect__select"
            :class="[
              {
                'multiselect__select--active': isLabelActive,
                'multiselect__select--disabled': disabled
              }
            ]"
            type="button"
            @mousedown.prevent.stop="toggle"
          >
            <font-awesome-icon :icon="['fas', 'chevron-down']" />
          </button>
          <!-- this is slot is used to add any slot from outside this Beva Select component -->
          <slot name="multiselect" />
        </template>
        <template #noOptions>
          <div v-if="isDenied && !!permission"
               class="eraby__no-permission">
            {{ $t('global.dropdown_menu.no_permission_to', { permission }) }}
          </div>
          <div v-else-if="isDenied"
               class="eraby__no-permission">
            {{ $t('global.dropdown_menu.no_permission') }}
          </div>
          <div v-else>
            {{ $t('global.dropdown_menu.list_is_empty') }}
          </div>
        </template>
        <!--Will be used if the items fetch asynchronous -->
        <template #afterList>
          <div
            v-if="meta.current_page < meta.last_page"
            v-observe-visibility="handleReachedEndOfList"
            class="multiselect__after-list"
          />
        </template>
      </vue-multiselect>
    </div>
    <div v-if="error && $slots.errors"
         class="eraby__errors">
      <slot name="errors" />
    </div>
  </div>
</template>

<script>
  import VueMultiselect from 'vue-multiselect';

  import { uuidv4 } from '@/helpers/uuidv4/uuidv4.helper';

  export default {
    name: 'BevaSelect',
    components: { VueMultiselect },
    props: {
      // Text
      labelText: {
        type: String,
        required: false,
      },
      placeholder: {
        type: String,
        required: false,
        default: 'Select Value ...',
      },
      hint: {
        type: String,
        required: false,
      },
      required: {
        type: Boolean,
        required: false,
        default: false,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      error: {
        type: Boolean,
        required: false,
        default: false,
      },
      multiple: {
        type: Boolean,
        required: false,
        default: false,
      },
      autoAddTag: {
        type: Boolean,
        required: false,
        default: true,
      },
      taggable: {
        type: Boolean,
        required: false,
        default: false,
      },
      loading: {
        type: Boolean,
        required: false,
        default: false,
      },
      limit: {
        type: Number,
        required: false,
        default: 3,
      },
      trackBy: {
        type: String,
        required: false,
        default: 'id',
      },
      label: {
        type: String,
        required: false,
        default: 'name',
      },
      // Data
      items: {
        type: Array,
        required: true,
      },
      searchable: {
        type: Boolean,
        required: false,
        default: true,
      },
      modelValue: {
        type: [Array, Object, String, null],
        required: true,
      },
      // is used for fetch items in asynchronous way
      meta: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      isDenied: {
        type: Boolean,
        default: false,
      },
      permission: {
        type: String,
        default: null,
      },
    },
    data() {
      return {
        isLabelActive: false,
        tags: [],
      };
    },
    computed: {
      options() {
        return [...this.tags, ...this.items];
      },
      selectedItems: {
        get() {
          return this.modelValue;
        },
        set(value) {},
      },
      arrowIconsColor() {
        // read CSS vars
        const rootStyles = getComputedStyle(document.documentElement);
        const baseColor = rootStyles.getPropertyValue('--color-base-500').trim();
        return this.disabled ? '#cccccc' : '#000';
      },
    },
    methods: {
      addTag(tag) {
        this.$emit('add-tag', tag);

        if (this.autoAddTag) {
          const newTag = {
            [this.label]: tag,
            [this.trackBy === this.label ? 'id' : this.trackBy]: uuidv4(),
          };
          this.tags.push(newTag);
          if (this.multiple) {
            const newValue = this.selectedItems ? [...this.selectedItems, newTag] : [newTag];
            this.$emit('update:modelValue', newValue);
          } else {
            this.$emit('update:modelValue', newTag);
          }
        }
      },
      handleLabelActive() {
        this.isLabelActive = true;
      },
      handleLabelDeactivate() {
        this.isLabelActive = false;
      },
      handleAsyncSearch(value) {
        this.$emit('async-search', value);
      },
      handleReachedEndOfList(reached) {
        if (Object.keys(this.meta).length > 0) {
          if (reached && this.meta.current_page <= this.meta.last_page) {
            this.$emit('async-fetch');
          }
        }
      },
    },
  };
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style lang="scss" scoped>
.eraby {
  &__wrapper {
    position: relative;
    &--disabled {
      background-color: var(--gray50, #f2f2f2);
      user-select: none;
      cursor: not-allowed;
    }
  }
  &__label {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    color: var(--gray500, #808080);
    padding: 4px;
    position: absolute;
    inset-inline-start: 12px;
    top: 0;
    transform: translateY(-50%);
    background-color: var(--shadesWhite, #fff);
    border-radius: 4px;
    z-index: 1;
    display: flex;
    align-items: center;
    line-height: 0;
    transition: color 300ms ease-in-out;
    &--error {
      color: var(--error500);
    }
    &--disabled {
      background-color: var(--gray50, #f2f2f2);
    }
    &--active {
      color: var(--color-base-500, #095ea6);
    }
  }
  &__required {
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 12px;
    color: var(--error500, #ff3b30);
    margin-inline-start: 4px;
  }
  &__hint {
    margin-inline-end: 4px;
  }
  &__selected {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;
    color: var(--gray900, #1a1a1a);
    border-radius: 4px;
    background-color: var(--gray50, #f2f2f2);
    z-index: 1;
  }
  &__errors {
    color: var(--error500, #ff3b30);
  }

  &__no-permission {
    color: var(--error500);
  }
}
/* Customize The Multiselect Library*/
:deep {
  .multiselect {
    box-sizing: border-box;
    height: 48px;
    padding-inline-end: 44px;
    padding: 4px;
    border-radius: 8px;
    border: 1px solid var(--gray500, #808080);
    background-color: var(--shadesWhite, #fff);
    transition: border-color 300ms ease-in-out;
    &--error {
      border-color: var(--error500);
    }
    &--active {
      border-color: var(--color-base-500, #095ea6);
      z-index: unset;
    }
    &--disabled {
      background-color: var(--gray50, #f2f2f2);
    }
    &__tags {
      padding: 0;
      border: none;
      background-color: transparent;
      cursor: pointer;
      overflow: hidden;
      display: flex;
      align-items: center;
      &-wrap {
        flex-shrink: 0;
        margin-inline-end: 4px;
      }
    }
    &__select,
    &__spinner {
      width: 40px;
      height: 40px;
      border-radius: 4px;
      background-color: var(--color-base-50, #eff7fe);
      inset-inline-end: 4px;
      top: 50%;
      transform: translateY(-50%);
    }
    &__select {
      display: flex;
      align-items: center;
      justify-content: center;
      transition: none;
      z-index: 1;
      color: var(--color-base-500);
      &--active {
        transform: translateY(-50%) rotateZ(180deg);
      }
      &--disabled {
        background-color: var(--gray100);
      }
      &::before {
        display: none;
      }
    }
    &__spinner {
      z-index: 99999;
    }
    &__spinner::before,
    &__spinner::after {
      border-color: var(--color-base-500) transparent transparent;
    }
    /* Customize the dropdown */
    &__content-wrapper {
      left: 0;
      margin-block: 4px;
      border-radius: 8px;
    }
    &__content {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    &__element {
      &:not(:last-child) {
        border-bottom: 1px solid var(--gray50);
      }
    }
    &__option {
      display: flex;
      align-items: center;
      padding: 8px;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 14px;
      color: var(--gray700, #4d4d4d);
      &--highlight,
      &--selected {
        // is used when hover in item in options list
        background: var(--color-base-50, #eff7fe);
      }
      // Remove [Press enter to select] text
      &::after {
        display: none;
      }
    }
    /* Customize search and placeholder */
    &__placeholder {
      padding: 0;
      margin: 0;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      color: var(--gray300, #b3b3b3);
    }
    &__input {
      padding: 0;
      margin: 0;
      &::placeholder {
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        color: var(--gray300, #b3b3b3);
      }
    }
    // Customize Chosen items
    &__single {
      padding: 0px;
      margin: 0px;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 14px;
      color: var(--gray900, #1a1a1a);
      background-color: transparent;
    }
    // Customize Chosen items
    &__tags-wrap {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    &__strong {
      flex-shrink: 0;
      margin-inline: 4px 8px;
      margin-block: 0;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 14px;
      color: var(--gray500, #808080);
    }
    &__after-list {
      height: 40px;
      background: var(--color-base-50, #eff7fe);
      width: 0;
      animation: highlightAnimation 0.7s infinite ease-in-out;
    }
    @keyframes highlightAnimation {
      0% {
        width: 0;
        background-color: var(--color-base-50, #eff7fe);
      }
      100% {
        width: 100%;
        background-color: unset;
      }
    }
  }
}
</style>
